import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Subtree } from "subtree.js/react";
import { pick } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import { EditScanController, type EditScanKind, type EditScanMode } from "./edit-scan-controller";
import { EditScanPage } from "./edit-scan-page";

const MODES: readonly EditScanMode[] = ["edit", "review", "approve"];

/**
 * Reads the scan id, `?mode=` and the `returnTo` navigation hint once; the
 * page never calls a router hook. `kind` is which section the URL is nested
 * under, so a refresh still returns to a sensible list.
 */
export function EditScanRoute({ deps, kind }: { deps: AppDeps; kind: EditScanKind }) {
  const { scanId } = useParams<{ scanId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const rawMode = params.get("mode");
  const mode = MODES.find((m) => m === rawMode) ?? (kind === "customer" ? "approve" : "review");
  // Prefer the explicit hint (a specific customer, the drafts list); otherwise
  // fall back to the section this URL is nested under.
  const hint = location.state as { returnTo?: string; queue?: readonly string[] } | null;
  const returnTo = hint?.returnTo ?? (kind === "customer" ? "/customer-scans" : "/scans");
  const queue = Array.isArray(hint?.queue) ? hint.queue : null;
  const base = kind === "customer" ? "/customer-scans" : "/scans";

  return (
    <Subtree
      key="edit-scan"
      deps={[scanId, kind, mode]}
      controller={() =>
        new EditScanController(
          { scanId: scanId ?? "", kind, mode, queue },
          pick(
            deps,
            "client",
            "authHandler",
            "storeRepository",
            "locationsRepository",
            "reviewQueueRepository",
            "customersRepository",
          ),
          {
            back: () => navigate(returnTo),
            open: (next) => navigate(`${base}/${next}/edit`, { replace: true, state: { returnTo, queue } }),
          },
        )
      }
    >
      <EditScanPage />
    </Subtree>
  );
}
